'use client';

/**
 * /p/[slug]/[tab] — renders the REAL tool page inside the project chrome.
 * No duplicated code: these are the same components as the top-level routes
 * (/portfolio, /seo, /funnels, ...), so all stats/analytics/data are intact,
 * and the project header + tabs (layout.tsx) stay visible for navigation.
 * Deep-link params (?funnel=, ?project=) are set by the layout's tab links.
 */
import { use, useEffect, useMemo, useState } from 'react';
import { notFound } from 'next/navigation';
import type { ProjectTab } from '@/lib/business-projects';
import { getBusinessProject, BUSINESS_PROJECTS } from '@/lib/business-projects';

import Link from 'next/link';
import SeoPage from '@/app/seo/page';
import ImprovePage from '@/app/improve/page';
import FactoryPage from '@/app/factory/page';
import FunnelsPage from '@/app/funnels/page';
import VideoLabPage from '@/app/funnels/video-lab/page';
import AbTestsPage from '@/app/funnels/ab-tests/page';
import AdsPage from '@/app/ads/page';
import ContentPage from '@/app/content/page';
import { ContentProvider } from '@/contexts/ContentContext';

/** /content gets its ContentProvider from app/content/layout.tsx — that layout
 * doesn't apply here, so mount the provider around the embedded page. */
function ProjectContent() {
  return (
    <ContentProvider>
      <div className="p-6"><ContentPage /></div>
    </ContentProvider>
  );
}
import CrmPage from '@/app/crm/page';
import EmailPage from '@/app/email/page';

/** Per-project websites grid — ONLY this project's sites (Portfolio stays global under Beast). */
function ProjectSites({ slug }: { slug: string }) {
  const project = getBusinessProject(slug)!;
  const sites = project.sites === 'factory' ? [] : project.sites;
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h2 className="text-sm font-medium text-gray-400 mb-3">Websites ({sites.length})</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {sites.map(s => (
          <div key={s.slug} className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
            {/* homepage screenshot — click launches the live site (Boss: same as portfolio) */}
            <a href={`https://${s.domain}`} target="_blank" rel="noopener noreferrer" className="block group">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={`/portfolio-screenshots/${s.slug}.png`} alt={s.domain}
                className="w-full aspect-video object-cover object-top group-hover:opacity-90 transition-opacity"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </a>
            <div className="p-4 space-y-2">
              <a href={`https://${s.domain}`} target="_blank" rel="noopener noreferrer" className="text-white font-medium hover:text-blue-300">{s.domain} ↗</a>
              <div className="flex gap-3 text-xs">
                <Link href={`/seo?site=${s.slug}`} className="text-gray-500 hover:text-blue-300">performance</Link>
                <Link href={`/improve?site=${s.slug}`} className="text-gray-500 hover:text-blue-300">improve</Link>
              </div>
            </div>
          </div>
        ))}
        {!sites.length && <div className="text-sm text-gray-500">No sites yet.</div>}
      </div>
    </main>
  );
}

/** VCTronics investors/labs pipeline — live from pitch.vctronics.com outreach API. */
function VctInvestors() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  useEffect(() => {
    fetch('/api/vctronics/investors').then(r => r.json())
      .then(d => setRows(Array.isArray(d) ? d : d.investors || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const types = useMemo(() => [...new Set(rows.map(r => r.type).filter(Boolean))].sort() as string[], [rows]);
  const statuses = useMemo(() => [...new Set(rows.map(r => r.outreach_status).filter(Boolean))].sort() as string[], [rows]);
  const filtered = rows.filter(r =>
    (!type || r.type === type) && (!status || r.outreach_status === status) &&
    (!q || `${r.name} ${r.contact} ${r.email} ${r.sector_focus}`.toLowerCase().includes(q.toLowerCase())));
  const STATUS_BADGE: Record<string, string> = {
    not_contacted: 'bg-gray-500/15 text-gray-400', emailed: 'bg-blue-500/15 text-blue-300',
    viewed_pitch: 'bg-amber-500/15 text-amber-300', replied: 'bg-green-500/15 text-green-300',
    meeting: 'bg-purple-500/15 text-purple-300', passed: 'bg-red-500/15 text-red-300',
  };
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <input placeholder="Search investors…" value={q} onChange={e => setQ(e.target.value)}
          className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-gray-600 w-64" />
        <select value={type} onChange={e => setType(e.target.value)} className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-gray-300">
          <option value="">All types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-gray-300">
          <option value="">All statuses</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="text-xs text-gray-500">{filtered.length} of {rows.length}</span>
        <a href="https://pitch.vctronics.com/admin/outreach" target="_blank" rel="noopener noreferrer"
          className="ml-auto px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-700 text-xs text-gray-300 hover:text-white">
          Full outreach admin ↗
        </a>
      </div>
      <div className="rounded-xl border border-gray-800 bg-gray-900/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-gray-500 text-xs">
            <tr>{['Name', 'Contact', 'Type', 'Fit', 'Status', 'Sector focus'].map(h => <th key={h} className="text-left px-4 py-2 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map(r => (
              <tr key={r.id} className="border-t border-gray-800/60 text-gray-300 hover:bg-gray-800/30">
                <td className="px-4 py-2 text-white">{r.name}</td>
                <td className="px-4 py-2">{r.contact || '—'}{r.email ? <span className="text-gray-500 text-xs block">{r.email}</span> : null}</td>
                <td className="px-4 py-2 text-gray-400 text-xs">{r.type || '—'}</td>
                <td className="px-4 py-2 text-xs">{r.fit || '—'}</td>
                <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_BADGE[r.outreach_status] || STATUS_BADGE.not_contacted}`}>{r.outreach_status}</span></td>
                <td className="px-4 py-2 text-gray-500 text-xs max-w-[280px] truncate">{r.sector_focus || '—'}</td>
              </tr>
            ))}
            {!loading && !filtered.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No investors match</td></tr>}
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading 700+ investors…</td></tr>}
          </tbody>
        </table>
      </div>
    </main>
  );
}

/** Funnel-overview KPI strip — the old standalone Overview page's stats,
 * embedded above the funnels list (Boss, 2026-07-07: "overview info and the
 * funnels list on the same page without navigating away"). */
function FunnelOverviewStrip({ funnelId }: { funnelId: string }) {
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`/api/funnels/${funnelId}/stats?days=30`)
      .then(r => r.json())
      .then(d => setOverview(d?.stats?.overview || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [funnelId]);
  if (loading) return <div className="px-6 pt-6 text-sm text-gray-500">Loading overview…</div>;
  if (!overview) return null;
  const cards = [
    { label: 'Visitors', value: overview.conversion?.startVisitors ?? '—' },
    { label: 'Leads', value: overview.leads?.last30Days ?? overview.leads?.total ?? '—' },
    { label: 'Booked', value: overview.appointments?.booked ?? '—' },
    { label: 'Video views', value: overview.videoViews?.last30Days ?? '—' },
    { label: 'Lead rate', value: overview.conversion?.visitorToLeadRate != null ? `${overview.conversion.visitorToLeadRate}%` : '—' },
    { label: 'Revenue', value: overview.revenue?.gross != null ? `$${overview.revenue.gross}` : '—' },
  ];
  return (
    <div className="px-6 pt-6">
      <h2 className="text-sm font-medium text-gray-400 mb-3">Overview — last 30 days</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {cards.map(({ label, value }) => (
          <div key={label} className="rounded-xl border border-gray-800 bg-gray-900/60 p-4">
            <div className="text-gray-500 text-xs mb-1">{label}</div>
            <div className="text-2xl font-semibold text-white">{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TAB_COMPONENTS: Record<Exclude<ProjectTab, 'websites' | 'email' | 'investors'>, React.ComponentType> = {
  performance: SeoPage,
  improve: ImprovePage,
  factory: FactoryPage,
  funnel: FunnelsPage,
  'video-lab': VideoLabPage,
  'ab-tests': AbTestsPage,
  ads: AdsPage,
  content: ProjectContent,
  crm: CrmPage,
};

export default function ProjectTabPage({ params }: { params: Promise<{ slug: string; tab: string }> }) {
  const { slug, tab } = use(params);
  const project = getBusinessProject(slug);
  if (!project || !project.tabs.includes(tab as ProjectTab)) return notFound();
  if (tab === 'websites') return <ProjectSites slug={slug} />;
  if (tab === 'investors') return <VctInvestors />;
  if (tab === 'performance') {
    // scope GSC to THIS project's domains — props, not URL params, so the
    // defaultTab redirect can't lose the scoping
    if (project.sites !== 'factory') return <SeoPage includeDomains={project.sites.map(s => s.domain)} />;
    const others = BUSINESS_PROJECTS.flatMap(p => (p.slug !== slug && p.sites !== 'factory' ? p.sites.map(s => s.domain) : []));
    return <SeoPage excludeDomains={others} />;
  }
  if (tab === 'crm' && project.sites !== 'factory') {
    // HARD scope: only this project's CRM sites (Boss 2026-07-29)
    return <CrmPage scopeDomains={project.sites.map(s => s.domain)} scopeName={project.name} />;
  }
  if (tab === 'email') {
    // HARD scope: only this project's mail — no other businesses visible
    return <EmailPage scope={project.emailKeys} scopeName={project.name} />;
  }
  const Tool = TAB_COMPONENTS[tab as Exclude<ProjectTab, 'websites' | 'email' | 'investors'>];
  if (tab === 'funnel' && project.funnelId) {
    return (<><FunnelOverviewStrip funnelId={project.funnelId} /><Tool /></>);
  }
  if (tab === 'ab-tests' && project.funnelId) {
    // HARD scope: only this project's funnel — Olga must never see Anna's splits
    return <AbTestsPage onlyFunnelId={project.funnelId} />;
  }
  if (tab === 'ads' && project.adsAccountId) {
    // HARD scope: only this project's ad account (Boss: never mix project data)
    return <AdsPage onlyAccountId={project.adsAccountId} />;
  }
  return <Tool />;
}
